import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Bookmark,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Play,
  Search,
  Video,
  X,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatVideoLength } from '@/lib/format';
import type { StudentLecture } from '@/types/student';
import {
  formatLectureDate,
  getLectureSubjectTheme,
  LectureCard,
  LecturePlayerView,
  LectureThumbnail,
  subjectTheme,
  type UnifiedLecture,
} from './lectureModule';

/**
 * A section's library as either panel receives it.
 *
 * `saved` is optional because it is the child's own shortlist: the student
 * panel is sent it, the parent panel is not.
 */
export interface LectureLibrary {
  section: { uuid: string; displayName: string } | null;
  lectures: (Omit<StudentLecture, 'saved'> & { saved?: boolean })[];
}

interface RecordedLecturesProps {
  list: LectureLibrary | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  /**
   * The child's own marks — bookmarking and "I have watched this". Supplied by
   * the student panel only. Without them the library is read-only: no
   * bookmark buttons, no Saved tab, and opening a lesson records nothing,
   * because a parent pressing play is not the child watching.
   */
  onSetSaved?: (uuid: string, saved: boolean) => Promise<unknown>;
  onMarkWatched?: (uuid: string) => Promise<unknown>;
  /** Whose library this is, when the reader is not the child themselves. */
  childName?: string;
  hideHeader?: boolean;
  className?: string;
  sectionName?: string;
  /**
   * Lessons a page. The student panel passes the child's own Settings choice;
   * the parent panel has no such setting and keeps the default.
   */
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 8;

export function RecordedLectures({
  list,
  isLoading,
  error,
  onRetry,
  onSetSaved,
  onMarkWatched,
  childName,
  hideHeader = false,
  pageSize = DEFAULT_PAGE_SIZE,
}: RecordedLecturesProps) {
  const canSave = onSetSaved !== undefined;

  // Navigation / Player State
  const [selectedLecture, setSelectedLecture] = useState<UnifiedLecture | null>(null);

  // Active Catalog Tab: 'ALL' | 'SAVED' (if bookmarking enabled)
  const [activeCatalogTab, setActiveCatalogTab] = useState<'ALL' | 'SAVED'>('ALL');

  // Filter & Search States
  const [selectedSubject, setSelectedSubject] = useState<string>('ALL');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  /**
   * Bookmarks this child has changed in this sitting.
   */
  const [savedOverrides, setSavedOverrides] = useState<Map<string, boolean>>(() => new Map());

  /**
   * Lessons opened in this sitting.
   */
  const [watchedUuids, setWatchedUuids] = useState<Set<string>>(() => new Set());

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);

  const handleToggleSave = async (uuid: string) => {
    if (!onSetSaved) return;
    const wasSaved = savedLectureUuids.has(uuid);
    const nowSaved = !wasSaved;

    setSavedOverrides((previous) => new Map(previous).set(uuid, nowSaved));

    try {
      await onSetSaved(uuid, nowSaved);
      if (nowSaved) {
        toast.success('Bookmark saved', 'Class saved to your collection');
      } else {
        toast.info('Bookmark removed', 'Class removed from your saved collection');
      }
    } catch (caught) {
      setSavedOverrides((previous) => new Map(previous).set(uuid, wasSaved));
      toast.error(
        'Could not save that bookmark',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    }
  };

  /**
   * Tells the server once per lesson per sitting.
   *
   * Stable, and guarded by the ref, because the player re-runs its effect
   * whenever this function changes: a fresh function each render — whose own
   * state update caused the next render — sent `/watched` in a loop until the
   * rate limiter answered 429.
   */
  const reportedWatched = useRef<Set<string>>(new Set());
  const handleMarkWatched = useCallback(
    (uuid: string) => {
      if (!onMarkWatched || reportedWatched.current.has(uuid)) return;
      reportedWatched.current.add(uuid);
      void onMarkWatched(uuid).catch(() => undefined);
      setWatchedUuids((previous) => new Set(previous).add(uuid));
    },
    [onMarkWatched],
  );

  const unifiedList = useMemo<UnifiedLecture[]>(
    () =>
      (list?.lectures ?? []).map((lecture) => ({
        uuid: lecture.uuid,
        title: lecture.title,
        subject: lecture.subject.name,
        teacher: lecture.teacher,
        date: lecture.postedOn,
        duration: formatVideoLength(lecture.durationSeconds),
        durationSeconds: lecture.durationSeconds,
        description: lecture.description || lecture.topic || '',
        className: list?.section?.displayName ?? null,
        videoUrl: lecture.playbackUrl ?? lecture.videoUrl,
        videoSource: lecture.videoSource,
        watched: lecture.watched || watchedUuids.has(lecture.uuid),
        saved: savedOverrides.get(lecture.uuid) ?? lecture.saved ?? false,
        thumbnailTheme: subjectTheme(lecture.subject.name),
      })),
    [list, watchedUuids, savedOverrides],
  );

  const savedLectureUuids = useMemo(
    () => new Set(unifiedList.filter((lecture) => lecture.saved).map((lecture) => lecture.uuid)),
    [unifiedList],
  );



  // Extract distinct subjects
  const availableSubjects = useMemo(() => {
    const subs = new Set<string>();
    unifiedList.forEach((l) => subs.add(l.subject));
    return Array.from(subs);
  }, [unifiedList]);

  // Extract distinct months (e.g., '2026-05' => 'May 2026')
  const availableMonths = useMemo(() => {
    const months = new Map<string, string>();
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    unifiedList.forEach((lec) => {
      if (lec.date) {
        const parts = lec.date.split('-');
        if (parts.length >= 2) {
          const key = `${parts[0]}-${parts[1]}`;
          const monthIndex = parseInt(parts[1], 10) - 1;
          if (monthIndex >= 0 && monthIndex < 12) {
            months.set(key, `${monthNames[monthIndex]} ${parts[0]}`);
          }
        }
      }
    });
    return Array.from(months.entries()).map(([key, label]) => ({ key, label }));
  }, [unifiedList]);

  // Filtered & Sorted Classes
  const filteredLectures = useMemo(() => {
    return unifiedList
      .filter((lec) => {
        // Tab filter (All vs Saved)
        if (activeCatalogTab === 'SAVED' && !savedLectureUuids.has(lec.uuid)) {
          return false;
        }

        // Calendar / Date filter (particular day)
        if (selectedDate && lec.date !== selectedDate) {
          return false;
        }

        // Subject filter
        if (selectedSubject !== 'ALL' && lec.subject !== selectedSubject) {
          return false;
        }

        // Month filter
        if (selectedMonth !== 'ALL' && !lec.date.startsWith(selectedMonth)) {
          return false;
        }

        // Search Filter
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          const matchTitle = lec.title.toLowerCase().includes(q);
          const matchSubject = lec.subject.toLowerCase().includes(q);
          const matchTeacher = lec.teacher.toLowerCase().includes(q);
          const matchDesc = lec.description.toLowerCase().includes(q);
          if (!matchTitle && !matchSubject && !matchTeacher && !matchDesc) return false;
        }

        return true;
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [
    unifiedList,
    activeCatalogTab,
    savedLectureUuids,
    selectedSubject,
    selectedDate,
    selectedMonth,
    searchQuery,
  ]);

  // Pagination calculations
  const totalFiltered = filteredLectures.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedLectures = filteredLectures.slice(startIndex, startIndex + pageSize);

  // If a lecture is open in player view
  if (selectedLecture) {
    const related = unifiedList.filter((l) => l.uuid !== selectedLecture.uuid);
    return (
      <LecturePlayerView
        lecture={selectedLecture}
        onBack={() => setSelectedLecture(null)}
        relatedLectures={related}
        onSelectLecture={(nextLec) => setSelectedLecture(nextLec)}
        isSaved={savedLectureUuids.has(selectedLecture.uuid)}
        onToggleSave={canSave ? (uuid) => void handleToggleSave(uuid) : undefined}
        onMarkWatched={onMarkWatched ? handleMarkWatched : undefined}
        childName={childName}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 max-w-full flex-1">
      {/* 1. Header Section */}
      {!hideHeader && (
        <div className="flex flex-col gap-1">
          <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Recorded Classes
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
            Watch previous class recordings, revision sessions and uploaded learning resources.
          </p>
        </div>
      )}

      {/* Optional Top Tabs for student shortlist */}
      {canSave && (
        <div className="flex items-center gap-4 sm:gap-6 border-b border-slate-300 dark:border-slate-700 pb-1 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => {
              setActiveCatalogTab('ALL');
              setCurrentPage(1);
            }}
            className={`pb-2.5 text-sm sm:text-lg font-bold transition-all cursor-pointer relative shrink-0 ${
              activeCatalogTab === 'ALL'
                ? 'text-slate-900 dark:text-white border-b-2 border-indigo-600'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 border-b-2 border-transparent'
            }`}
          >
            All Classes
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveCatalogTab('SAVED');
              setCurrentPage(1);
            }}
            className={`pb-2.5 flex items-center gap-1.5 text-sm sm:text-lg font-bold transition-all cursor-pointer relative shrink-0 ${
              activeCatalogTab === 'SAVED'
                ? 'text-slate-900 dark:text-white border-b-2 border-indigo-600'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 border-b-2 border-transparent'
            }`}
          >
            <Bookmark className="h-3.5 w-3.5" />
            <span>Saved</span>
          </button>
        </div>
      )}

      {/* 2. Filter Bar */}
      <div className="rounded-2xl bg-white dark:bg-[#121829] border border-slate-300 dark:border-slate-700 p-3 shadow-xs flex flex-col md:flex-row md:items-end gap-2.5 sm:gap-3">
        {/* 1. Search Bar (First) */}
        <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Search</span>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by title, subject or teacher..."
              className="h-[38px] w-full bg-slate-50 dark:bg-white/5 border border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 text-xs sm:text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-xl pl-10 pr-8 py-2 font-medium outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 cursor-pointer"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* 2. Subject */}
        <div className="flex flex-col gap-1 w-full md:w-44 lg:w-48 shrink-0">
          <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Subject</span>
          <div className="relative">
            <select
              value={selectedSubject}
              onChange={(e) => {
                setSelectedSubject(e.target.value);
                setCurrentPage(1);
              }}
              className="h-[38px] appearance-none w-full bg-slate-50 dark:bg-white/5 border border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 rounded-xl px-3 py-2 pr-7 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer truncate"
            >
              <option value="ALL" className="bg-white dark:bg-[#151928] text-slate-800 dark:text-white">All Subjects</option>
              {availableSubjects.map((sub) => (
                <option key={sub} value={sub} className="bg-white dark:bg-[#151928] text-slate-800 dark:text-white">
                  {sub}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* 3. Date (Decreased width, colored calendar icon, "Pick a date" placeholder) */}
        <div className="flex flex-col gap-1 w-full md:w-36 lg:w-40 shrink-0">
          <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Date</span>
          <DatePicker
            label="Date"
            hideLabel
            size="sm"
            isClearable
            placeholder="Pick a date"
            icon={<CalendarDays className="shrink-0 h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" aria-hidden />}
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setCurrentPage(1);
            }}
            className="h-[38px] w-full text-xs sm:text-sm font-semibold rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 text-slate-700 dark:text-slate-200"
          />
        </div>

        {/* 4. Month */}
        <div className="flex flex-col gap-1 w-full md:w-40 lg:w-44 shrink-0">
          <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Month</span>
          <div className="relative">
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setCurrentPage(1);
              }}
              className="h-[38px] appearance-none w-full bg-slate-50 dark:bg-white/5 border border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 rounded-xl px-3 py-2 pr-7 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer truncate"
            >
              <option value="ALL" className="bg-white dark:bg-[#151928] text-slate-800 dark:text-white">All Months</option>
              {availableMonths.map((m) => (
                <option key={m.key} value={m.key} className="bg-white dark:bg-[#151928] text-slate-800 dark:text-white">
                  {m.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* 5. Clear Button (Red color for filters, small width) */}
        {(() => {
          const isFiltersActive = selectedSubject !== 'ALL' || selectedDate !== '' || selectedMonth !== 'ALL';
          return (
            <div className="flex flex-col gap-1 shrink-0">
              <span className="text-sm font-semibold invisible select-none" aria-hidden="true">Clear</span>
              <button
                type="button"
                disabled={!isFiltersActive}
                onClick={() => {
                  setSelectedSubject('ALL');
                  setSelectedDate('');
                  setSelectedMonth('ALL');
                  setCurrentPage(1);
                }}
                className={`h-[38px] px-3.5 text-xs sm:text-sm font-semibold rounded-xl border transition-all shrink-0 flex items-center justify-center ${
                  isFiltersActive
                    ? 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/30 hover:border-red-300 cursor-pointer'
                    : 'text-slate-400 dark:text-slate-600 border-slate-200 dark:border-slate-700 cursor-not-allowed opacity-50'
                }`}
              >
                Clear
              </button>
            </div>
          );
        })()}
      </div>

      {/* 3. Stats & View Toggle */}
      <div className="flex items-center justify-between pt-1">
        <div className="flex flex-row items-center gap-2">
          <span className="text-lg font-semibold text-slate-500 dark:text-slate-500">Total Recordings : </span>
          <span className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {totalFiltered}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            className={`px-3.5 py-1.5 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all cursor-pointer ${
              viewMode === 'grid'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-white dark:bg-[#121829] text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-700 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
            <span>Grid View</span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`px-3.5 py-1.5 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all cursor-pointer ${
              viewMode === 'list'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-white dark:bg-[#121829] text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-700 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <List className="h-4 w-4" />
            <span>List View</span>
          </button>
        </div>
      </div>

      {/* 4. Loading State */}
      {isLoading && (
        <div className="rounded-2xl bg-white dark:bg-[#121829] border border-slate-300 dark:border-slate-700 p-6 shadow-xs">
          <SkeletonLines count={8} />
        </div>
      )}

      {/* 5. Error State */}
      {!isLoading && error && (
        <div className="rounded-2xl bg-white dark:bg-[#121829] border border-slate-300 dark:border-slate-700 p-6 flex flex-col items-start gap-3 shadow-xs">
          <Alert
            tone="critical"
            title={childName ? `Could not load ${childName}'s classes` : 'Could not load your classes'}
          >
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}

      {/* 6. Classes Grid View */}
      {!isLoading && !error && paginatedLectures.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paginatedLectures.map((lecture) => (
            <LectureCard
              key={lecture.uuid}
              lecture={lecture}
              onSelect={(lec) => setSelectedLecture(lec)}
              isSaved={savedLectureUuids.has(lecture.uuid)}
              onToggleSave={canSave ? (uuid) => void handleToggleSave(uuid) : undefined}
            />
          ))}
        </div>
      )}

      {/* 7. Classes List View */}
      {!isLoading && !error && paginatedLectures.length > 0 && viewMode === 'list' && (
        <div className="flex flex-col gap-3">
          {paginatedLectures.map((lecture) => {
            const theme = getLectureSubjectTheme(lecture.subject);
            return (
              <div
                key={lecture.uuid}
                onClick={() => setSelectedLecture(lecture)}
                className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-3.5 rounded-2xl bg-slate-100/80 dark:bg-[#151928] border border-slate-200 dark:border-slate-800 hover:border-indigo-500/50 dark:hover:border-indigo-500/40 transition-all cursor-pointer shadow-xs"
              >
                <div className="flex items-center gap-3.5 min-w-0 flex-1 w-full sm:w-auto">
                  <div className="relative h-16 w-28 rounded-xl bg-slate-900 overflow-hidden shrink-0">
                    <LectureThumbnail theme={lecture.thumbnailTheme} title={lecture.title} />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                      <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-white/95 text-indigo-600 flex items-center justify-center shadow-md">
                        <Play className="h-3 w-3 sm:h-3.5 sm:w-3.5 ml-0.5 fill-indigo-600 text-indigo-600" />
                      </div>
                    </div>
                    {lecture.duration && (
                      <div className="absolute bottom-1 right-1 px-1 rounded bg-black/85 text-[10px] font-bold text-white">
                        {lecture.duration}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] sm:text-[11px] font-bold uppercase px-2 py-0.5 rounded-md border ${theme.pill}`}>
                        {lecture.subject}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-medium truncate">{lecture.teacher}</span>
                    </div>
                    <h4 className="text-base font-bold text-slate-800 dark:text-slate-100 line-clamp-2 capitalize group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors mt-1">
                      {lecture.title}
                    </h4>
                    {lecture.description && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">
                        {lecture.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100 dark:border-white/5">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{formatLectureDate(lecture.date)}</span>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 8. Empty State */}
      {!isLoading && !error && paginatedLectures.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-[#121829]/80 backdrop-blur-sm p-8 text-center flex flex-col items-center justify-center shadow-xs">
          <div className="h-12 w-12 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200/60 dark:border-indigo-500/20 flex items-center justify-center text-indigo-500 dark:text-indigo-400 mb-3 shadow-2xs">
            <Video className="h-6 w-6" />
          </div>
          <h4 className="text-base font-bold text-slate-800 dark:text-slate-200">
            {activeCatalogTab === 'SAVED'
              ? 'No saved classes yet'
              : unifiedList.length === 0
                ? 'No classes have been posted yet'
                : 'No recorded classes found'}
          </h4>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-medium max-w-md">
            {activeCatalogTab === 'SAVED'
              ? 'Click the bookmark icon on any class card to save it for quick access later.'
              : unifiedList.length === 0
                ? childName
                  ? `When ${childName}'s teachers upload a recording of a lesson, it will appear here to watch any time.`
                  : 'When your teachers upload a recording of a lesson, it will appear here for you to watch any time.'
                : 'Try searching for a different keyword, subject or teacher.'}
          </p>
        </div>
      )}

      {/* 9. Pagination Footer */}
      {!isLoading && !error && totalFiltered > 0 && (
        <div className="mt-auto sticky bottom-0 z-20 -mx-3.5 sm:-mx-5 lg:-mx-6 -mb-3.5 sm:-mb-5 lg:-mb-6 px-3.5 sm:px-5 lg:px-6 py-3.5 bg-[#f8f8fc]/95 dark:bg-[#111225]/95 backdrop-blur-md border-t border-slate-200/80 dark:border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]">
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
            Showing {startIndex + 1} - {Math.min(startIndex + pageSize, totalFiltered)} of{' '}
            {totalFiltered} classes
          </span>

          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar max-w-full py-1">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="h-8 w-8 rounded-xl bg-white dark:bg-[#121829] border border-slate-300 dark:border-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer shadow-2xs shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                onClick={() => setCurrentPage(pageNum)}
                className={`h-8 min-w-8 px-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${
                  safePage === pageNum
                    ? 'bg-indigo-600 text-white shadow-xs scale-105'
                    : 'bg-white dark:bg-[#121829] border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
                }`}
              >
                {pageNum}
              </button>
            ))}

            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="h-8 w-8 rounded-xl bg-white dark:bg-[#121829] border border-slate-300 dark:border-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer shadow-2xs shrink-0"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
